import dayjs from 'dayjs';
import {
  faCircleXmark,
  faFlag,
  faMicrochip,
  faTriangleExclamation,
  faTrophy,
} from '@fortawesome/pro-solid-svg-icons';

import { tv, VariantProps } from '@utils';

import { Badge, Icon, Render, Tooltip } from '@components';

const variants = tv({
  slots: {
    container: 'flex w-max items-center gap-1.5',
    createdAt: 'text-caption-3',
    icon: '',
  },
  variants: {
    variant: {
      default: {
        icon: 'h-4 w-4 text-[14px]',
      },
      details: {
        container: 'gap-2 !bg-white px-5 py-1.5 text-body',
        icon: 'h-5 w-5 text-[20px]',
        createdAt: 'text-neural-04 leading-normal',
      },
    },
    stretch: {
      true: {
        container: 'h-10',
      },
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type Props = VariantProps<typeof variants> & {
  status: string;
  createdAt?: string;
  description?: string | null;
};

export const BadgeStatus = ({
  status,
  createdAt,
  description,
  variant,
  stretch,
}: Props) => {
  const variantSlots = variants({ variant, stretch });

  const icon = (() => {
    switch (status) {
      case 'healthy':
      case 'succeeded':
        return faTrophy;

      case 'queued':
      case 'pending':
        return faFlag;

      case 'cancelled':
      case 'uninstalled':
        return faCircleXmark;

      case 'failed':
      case 'errored':
        return faTriangleExclamation;

      default:
        return faMicrochip;
    }
  })();

  const badgeVariant = (() => {
    switch (status) {
      case 'healthy':
      case 'succeeded':
        return 'green';

      case 'queued':
      case 'pending':
      case 'cancelled':
      case 'uninstalled':
        return 'yellow';

      case 'failed':
      case 'errored':
        return 'red';

      default:
        return 'grey';
    }
  })();

  return (
    <Badge className={variantSlots.container()} variant={badgeVariant}>
      <Icon icon={icon} className={variantSlots.icon()} />

      <Tooltip disabled={!description} className="w-60">
        <div slot="trigger" title={description ?? ''}>
          <p className="capitalize">{status}</p>

          <Render if={createdAt}>
            <p className={variantSlots.createdAt()}>
              {dayjs(createdAt).fromNow()}
            </p>
          </Render>
        </div>

        {description}
      </Tooltip>
    </Badge>
  );
};
